import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import ModalLayoutBase, { MobileModalType } from 'src/components/ModalLayout';
import useSelectedTab from 'src/hooks/useSelectedTab';
import { initializePushNotification } from 'src/push';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { ToastType } from 'src/types/Toast';
import { pushEvent } from 'src/utils/event-gateway';
const ModalLayout = styled(ModalLayoutBase)`
  background-color: transparent !important;
  min-width: unset;
`;
const ModalBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const Container = styled.div`
  width: 360px;
  display: flex;
  flex-direction: column;
  background: ${({
  theme
}) => theme.color.bg2};
  border-radius: 24px;
  overflow: hidden;

  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 100%;
    max-width: 360px;
  }
`;
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 24px 8px;
`;
const Title = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  color: ${({
  theme
}) => theme.color.content12};
`;
const Description = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content9};
`;
const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px 24px;
`;
const AllowButton = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  padding: 0 16px;

  background-color: ${({
  theme
}) => theme.color.primary1};
  border-radius: 360px;

  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.contentInverse};
  text-align: center;
`;
const MaybeLaterButton = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 4px;

  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content8};
  text-align: center;
`;
const PushNotificationPermissionModal = () => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const showToast = useSetAtom(showToastAtom);
  const {
    selectedTab
  } = useSelectedTab();
  const handleClose = () => {
    pushEvent({
      eventType: EVENT_TYPE.PUSH_NOTIFICATION,
      eventName: EVENT_NAME.ANYWHERE__CLICK_PUSH_PERMISSION_POPUP_BUTTON,
      eventParams: {
        action_category: 'click',
        tab: selectedTab.name,
        page: 'main',
        target: 'push_permission_popup_button',
        button_type: 'denied'
      }
    });
    closeModal(ModalType.PUSH_NOTIFICATION_PERMISSION);
    showToast({
      type: ToastType.SUCCESS,
      message: t('popup.push_noti_block_toast')
    });
  };
  const handleAllow = async () => {
    pushEvent({
      eventType: EVENT_TYPE.PUSH_NOTIFICATION,
      eventName: EVENT_NAME.ANYWHERE__CLICK_PUSH_PERMISSION_POPUP_BUTTON,
      eventParams: {
        action_category: 'click',
        tab: selectedTab.name,
        page: 'main',
        target: 'push_permission_popup_button',
        button_type: 'granted'
      }
    });
    closeModal(ModalType.PUSH_NOTIFICATION_PERMISSION);
    await initializePushNotification();
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.PUSH_NOTIFICATION,
      eventName: EVENT_NAME.ANYWHERE__IMPRESSION_PUSH_PERMISSION_POPUP,
      eventParams: {
        action_category: 'impression',
        tab: selectedTab.name,
        page: 'main',
        target: 'push_permission_popup'
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <ModalLayout isDim mobileType={MobileModalType.SCREEN_MODAL} lockBodyScroll closeWhenClickOutside={false} closeWithESC={false} navButtonType={ModalNavButtonType.NONE} mobileNavButtonType={ModalNavButtonType.NONE} data-sentry-element="ModalLayout" data-sentry-component="PushNotificationPermissionModal" data-sentry-source-file="index.tsx">
      <ModalBody data-sentry-element="ModalBody" data-sentry-source-file="index.tsx">
        <Container data-sentry-element="Container" data-sentry-source-file="index.tsx">
          <Header data-sentry-element="Header" data-sentry-source-file="index.tsx">
            <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">{t('popup.push_noti_title')}</Title>
            <Description data-sentry-element="Description" data-sentry-source-file="index.tsx">{t('popup.push_noti_desc')}</Description>
          </Header>
          <ButtonGroup data-sentry-element="ButtonGroup" data-sentry-source-file="index.tsx">
            <AllowButton onClick={handleAllow} data-sentry-element="AllowButton" data-sentry-source-file="index.tsx">{t('popup_push_noti_cta_allow')}</AllowButton>
            <MaybeLaterButton onClick={handleClose} data-sentry-element="MaybeLaterButton" data-sentry-source-file="index.tsx">
              {t('popup_push_noti_cta_later')}
            </MaybeLaterButton>
          </ButtonGroup>
        </Container>
      </ModalBody>
    </ModalLayout>;
};
export default PushNotificationPermissionModal;